// frontend/src/services/web3Service.js
import { ethers } from "ethers";

/**
 * Service Web3 pour gérer la connexion blockchain et les contrats
 * Implémente un pattern singleton pour partager l'état Web3 global
 * @class
 */
class Web3Service {
  constructor() {
    this.provider = null;
    this.signer = null;
    this.contracts = {};
    this.contractAddresses = null;
  }

  /**
   * Initialise le provider Web3
   */
  async initProvider() {
    try {
      // Essaie d'utiliser MetaMask si disponible
      if (window.ethereum) {
        this.provider = new ethers.BrowserProvider(window.ethereum);
        console.log("✅ Provider MetaMask initialisé");
        return this.provider;
      }

      // Sinon, utilise le provider local Hardhat via le Proxy Vite (Silver Bullet)
      const rpcUrl = window.location.origin + "/rpc";
      this.provider = new ethers.JsonRpcProvider(rpcUrl);
      console.log("✅ Provider JSON-RPC initialisé:", rpcUrl);
      return this.provider;
    } catch (error) {
      console.error("❌ Erreur d'initialisation du provider:", error);
      throw error;
    }
  }

  /**
   * Connecte un wallet (MetaMask)
   */
  async connectWallet() {
    if (!window.ethereum) {
      throw new Error("MetaMask n'est pas installé");
    }

    try {
      await window.ethereum.request({ method: "eth_requestAccounts" });
      this.provider = new ethers.BrowserProvider(window.ethereum);
      this.signer = await this.provider.getSigner();
      const address = await this.signer.getAddress();
      console.log("✅ Wallet connecté:", address);
      return { provider: this.provider, signer: this.signer, address };
    } catch (error) {
      console.error("❌ Erreur de connexion wallet:", error);
      throw error;
    }
  }

  /**
   * Charge les adresses des contrats
   * FIX: Utilise le fichier statique généré par le deployer au lieu de l'API Backend
   */
  async loadContractAddresses() {
    try {
      // FIX: Utilise le fichier statique généré par le deployer au lieu de l'API Backend
      const response = await fetch("/contracts/deployed_contracts.json");

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status} - Vérifier que le deployer a bien généré le fichier json`);
      }

      const data = await response.json();
      this.contractAddresses = data;
      console.log("✅ Adresses des contrats chargées:", this.contractAddresses);
      return this.contractAddresses;
    } catch (error) {
      console.error("❌ Erreur de chargement des adresses:", error);
      throw error;
    }
  }

  /**
   * Charge l'ABI d'un contrat (depuis les données statiques déjà chargées)
   */
  async loadContractABI(contractName) {
    try {
      // On vérifie si on a déjà les infos (chargées via loadContractAddresses)
      if (!this.contractAddresses) {
        await this.loadContractAddresses();
      }

      // On récupère l'ABI directement depuis l'objet local
      const contractData = this.contractAddresses[contractName];

      if (!contractData || !contractData.abi) {
        throw new Error(`ABI introuvable pour ${contractName} dans le fichier JSON`);
      }

      console.log(`✅ ABI de ${contractName} récupéré depuis le cache`);
      return contractData.abi;

    } catch (error) {
      console.error(`❌ Erreur de récupération ABI ${contractName}:`, error);
      throw error;
    }
  }

  /**
   * Initialise un contrat
   */
  async initContract(contractName) {
    try {
      if (!this.provider) {
        await this.initProvider();
      }

      if (!this.contractAddresses) {
        await this.loadContractAddresses();
      }

      const address = this.contractAddresses[contractName]?.address || this.contractAddresses[contractName];
      if (!address || typeof address !== "string") {
        throw new Error(`Adresse du contrat ${contractName} introuvable`);
      }

      const abi = await this.loadContractABI(contractName);

      const contract = new ethers.Contract(
        address,
        abi,
        this.signer || this.provider
      );

      this.contracts[contractName] = contract;
      console.log(`✅ Contrat ${contractName} initialisé à ${address}`);
      return contract;
    } catch (error) {
      console.error(`❌ Erreur d'initialisation du contrat ${contractName}:`, error);
      throw error;
    }
  }

  /**
   * Récupère un contrat (l'initialise si nécessaire)
   */
  async getContract(contractName) {
    if (!this.contracts[contractName]) {
      await this.initContract(contractName);
    }
    return this.contracts[contractName];
  }

  /**
   * Récupère les informations du réseau
   */
  async getNetworkInfo() {
    if (!this.provider) {
      await this.initProvider();
    }

    const network = await this.provider.getNetwork();
    const blockNumber = await this.provider.getBlockNumber();

    return {
      chainId: Number(network.chainId),
      name: network.name,
      blockNumber,
    };
  }

  /**
   * Récupère le solde d'une adresse
   */
  async getBalance(address) {
    if (!this.provider) {
      await this.initProvider();
    }

    const balance = await this.provider.getBalance(address);
    return ethers.formatEther(balance);
  }
}

// Instance singleton
const web3Service = new Web3Service();

export default web3Service;
